import {
    CSSProperties,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../../utils/utils';

export interface SearchableSelectOption {
    value: string;
    /** Primary line — also what the trigger shows once selected. */
    label: string;
    /** Secondary line rendered under the label in the list. */
    description?: string;
    /** Extra text the search matches but never displays (e.g. an id). */
    keywords?: string;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    options: SearchableSelectOption[];
    /** Trigger text while nothing is selected. */
    placeholder: string;
    searchPlaceholder: string;
    /** Shown when the filter matches nothing. */
    emptyMessage: string;
    disabled?: boolean;
    /** Rendered as the last row of the panel — e.g. a "create new" action. */
    footer?: ReactNode;
    className?: string;
}

// Panel sizing. The panel is positioned against the viewport (see below), so
// these bound it between "still useful" and "doesn't dominate the screen".
const MAX_PANEL_HEIGHT = 380;
const MIN_PANEL_HEIGHT = 180;
const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 4;

/**
 * Single-select picker with a search box and two-line options.
 *
 * The native `<select>` it replaces could only show one flat line per option
 * and had no filter, so a list of problem reports was unusable: entries
 * without a booking number showed a raw id fragment, there was no created-at
 * date to judge recency by, and finding a specific report meant scrolling the
 * whole list.
 *
 * The panel is `position: fixed` and measured against the viewport rather than
 * laid out inside the field. Its first caller lives in `CustomModal`, whose
 * body is an `overflow-y-auto` scroll container — an absolutely positioned
 * panel gets clipped at that container's edge. Fixed positioning escapes the
 * clip (no ancestor establishes a containing block for it), and measuring lets
 * the panel flip above the trigger when there isn't room below.
 */
const SearchableSelectField = ({
    value,
    onChange,
    options,
    placeholder,
    searchPlaceholder,
    emptyMessage,
    disabled = false,
    footer,
    className,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const selected = options.find((option) => option.value === value) ?? null;

    const filtered = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!term) return options;
        return options.filter((option) =>
            `${option.label} ${option.description ?? ''} ${option.keywords ?? ''}`
                .toLowerCase()
                .includes(term));
    }, [options, query]);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN - TRIGGER_GAP;
        const spaceAbove = rect.top - VIEWPORT_MARGIN - TRIGGER_GAP;
        // Prefer opening downwards; flip up only when that would squeeze the
        // panel below the useful minimum and there is genuinely more room above.
        const flipUp = spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow;
        const available = flipUp ? spaceAbove : spaceBelow;
        setPanelStyle({
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            maxHeight: Math.min(Math.max(available, MIN_PANEL_HEIGHT), MAX_PANEL_HEIGHT),
            ...(flipUp
                ? { bottom: window.innerHeight - rect.top + TRIGGER_GAP }
                : { top: rect.bottom + TRIGGER_GAP }),
        });
    }, []);

    // Measure before paint so the panel never renders in the wrong place, then
    // keep it attached while the modal body or the window moves under it.
    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        // Capture phase: the scroll happens on the modal body, not on window.
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, updatePosition]);

    // Close on an outside click or Escape — the panel is a plain div, so
    // nothing else dismisses it. It stays a DOM descendant of the container
    // despite being fixed-positioned, so `contains` still recognises clicks
    // inside the panel.
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    // Reset the filter on each open and put the caret in the search box, so
    // the picker is type-to-find without an extra click.
    useEffect(() => {
        if (!open) return;
        setQuery('');
        setHighlighted(0);
        searchRef.current?.focus();
    }, [open]);

    const select = (option: SearchableSelectOption) => {
        onChange(option.value);
        setOpen(false);
    };

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((i) => Math.min(i + 1, filtered.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((i) => Math.max(i - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const option = filtered[highlighted];
            if (option) select(option);
        }
    };

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <button
                ref={triggerRef}
                type={'button'}
                disabled={disabled}
                onClick={() => setOpen((isOpen) => !isOpen)}
                className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm',
                    'focus:outline-none',
                    open ? 'border-primary' : 'border-foreground/15',
                    disabled && 'cursor-not-allowed opacity-60',
                )}
            >
                <span className={cn('truncate', !selected && 'text-foreground/50')}>
                    {selected?.label ?? placeholder}
                </span>
                <ChevronDown
                    className={cn(
                        'h-4 w-4 shrink-0 text-foreground/60 transition-transform duration-200',
                        open && 'rotate-180',
                    )}
                />
            </button>

            {open && (
                <div
                    style={panelStyle}
                    className={
                        'z-[1500] flex flex-col overflow-hidden rounded-md ' +
                        'border border-foreground/15 bg-white shadow-lg'
                    }
                >
                    <div className={'flex shrink-0 items-center gap-2 border-b border-foreground/10 px-3 py-2'}>
                        <Search className={'h-4 w-4 shrink-0 text-foreground/40'} />
                        <input
                            ref={searchRef}
                            className={'w-full bg-transparent text-sm outline-none placeholder:text-foreground/40'}
                            value={query}
                            placeholder={searchPlaceholder}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setHighlighted(0);
                            }}
                            onKeyDown={onSearchKeyDown}
                        />
                    </div>
                    <ul className={'min-h-0 flex-1 overflow-y-auto py-1'}>
                        {filtered.length === 0 && (
                            <li className={'px-3 py-3 text-sm text-foreground/50'}>{emptyMessage}</li>
                        )}
                        {filtered.map((option, index) => (
                            <li key={option.value}>
                                <button
                                    type={'button'}
                                    onClick={() => select(option)}
                                    onMouseEnter={() => setHighlighted(index)}
                                    className={cn(
                                        'flex w-full items-start justify-between gap-2 px-3 py-2 text-left',
                                        index === highlighted && 'bg-foreground/5',
                                    )}
                                >
                                    {/* Options wrap rather than truncate: on a phone
                                        the panel is barely wider than the modal, and
                                        a clipped "#B-00000332A · Zahlungs- oder Buch…"
                                        hides exactly the part that distinguishes one
                                        report from the next. The trigger above still
                                        truncates — it is a single row. */}
                                    <span className={'min-w-0'}>
                                        <span className={'block break-words text-sm text-foreground'}>
                                            {option.label}
                                        </span>
                                        {option.description && (
                                            <span className={'mt-0.5 block break-words text-xs text-foreground/50'}>
                                                {option.description}
                                            </span>
                                        )}
                                    </span>
                                    {option.value === value && (
                                        <Check className={'mt-0.5 h-4 w-4 shrink-0 text-primary'} />
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {footer && (
                        <div className={'shrink-0 border-t border-foreground/10'}>{footer}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelectField;
